

import React, { useRef, useState } from 'react';
import './Tool1Page.css';


// Feature button style (must be defined before use)
const featureBtnStyle = {
  background: '#f5f6fa',
  color: '#2355d6',
  border: '1.5px solid #bfc7d1',
  borderRadius: 7,
  padding: '4px 10px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  transition: 'background 0.2s, color 0.2s',
  margin: 0,
  outline: 'none',
  minWidth: 70,
  boxShadow: '0 1px 4px rgba(79,140,255,0.04)',
  letterSpacing: 0.5,
  marginBottom: 0,
  marginTop: 0,
  marginRight: 0,
  marginLeft: 0,
  opacity: 1,
  pointerEvents: 'auto',
};

const Tool1Page = () => {
  // Show/hide Crop button state
  const [showCropBtn, setShowCropBtn] = useState(true);
  // Image state (width/height must be defined first)
  const [image, setImage] = useState(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rotate, setRotate] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [imgInfo, setImgInfo] = useState({ width: null, height: null, size: null });
  const [fileName, setFileName] = useState('');
  // Image format selection
  const [downloadFormat, setDownloadFormat] = useState('png');
  const canvasRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  // Professional crop state and handlers
  const [cropMode, setCropMode] = useState(false);
  const [cropRect, setCropRect] = useState(null); // {x, y, w, h}
  const [cropAction, setCropAction] = useState(null); // 'move', 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw', or null
  const [cropStartPoint, setCropStartPoint] = useState(null); // {x, y}
  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        handleFile(file);
      }
    }
  };
  // Helper: get mouse position relative to image
  const getImgPos = (e) => {
    let imgElem = e.target;
    // Try to find the image element
    if (!imgElem.classList || !imgElem.classList.contains('crop-img')) {
      imgElem = document.querySelector('.crop-img');
    }
    if (!imgElem) return { x: 0, y: 0 };
    const rect = imgElem.getBoundingClientRect();
    let clientX = e.clientX, clientY = e.clientY;
    if (e.touches && e.touches.length) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }
    const x = ((clientX - rect.left) / rect.width) * width;
    const y = ((clientY - rect.top) / rect.height) * height;
    return { x, y };
  };
  // Start crop: new or drag/resize (single definition, supports mouse/touch)
  const handleCropMouseDown = (e) => {
    if (!cropMode) return;
    e.preventDefault();
    // If clicking a handle, set action
    const handle = e.target.getAttribute('data-crophandle');
    if (handle && cropRect) {
      setCropAction(handle);
      setCropStartPoint(getImgPos(e));
      return;
    }
    // If clicking inside crop rect, move
    if (cropRect) {
      const { x, y } = getImgPos(e);
      if (
        x >= cropRect.x && x <= cropRect.x + cropRect.w &&
        y >= cropRect.y && y <= cropRect.y + cropRect.h
      ) {
        setCropAction('move');
        setCropStartPoint({ x, y, orig: { ...cropRect } });
        return;
      }
    }
    // Otherwise, start new crop
    const { x, y } = getImgPos(e);
    setCropRect({ x, y, w: 0, h: 0 });
    setCropAction('new');
    setCropStartPoint({ x, y });
  };
  // Move/resize crop (global for smoothness)
  React.useEffect(() => {
    if (!cropMode || !cropAction) return;
    const handleMove = (e) => {
      e.preventDefault();
      let clientX = e.clientX, clientY = e.clientY;
      if (e.touches && e.touches.length) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      }
      let imgElem = document.querySelector('.crop-img');
      if (!imgElem) return;
      const rect = imgElem.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * width;
      const y = ((clientY - rect.top) / rect.height) * height;
      if (cropAction === 'new' && cropStartPoint) {
        setCropRect({
          x: Math.min(cropStartPoint.x, x),
          y: Math.min(cropStartPoint.y, y),
          w: Math.abs(x - cropStartPoint.x),
          h: Math.abs(y - cropStartPoint.y)
        });
      } else if (cropAction === 'move' && cropStartPoint && cropRect) {
        const dx = x - cropStartPoint.x;
        const dy = y - cropStartPoint.y;
        setCropRect({
          x: Math.max(0, Math.min(width - cropRect.w, cropStartPoint.orig.x + dx)),
          y: Math.max(0, Math.min(height - cropRect.h, cropStartPoint.orig.y + dy)),
          w: cropRect.w,
          h: cropRect.h
        });
      } else if (cropRect && cropStartPoint) {
        // Resize from handle
        let { x: rx, y: ry, w, h } = cropRect;
        let nx = rx, ny = ry, nw = w, nh = h;
        switch (cropAction) {
          case 'n': nh = h + (ry - y); ny = y; break;
          case 's': nh = y - ry; break;
          case 'w': nw = w + (rx - x); nx = x; break;
          case 'e': nw = x - rx; break;
          case 'nw': nh = h + (ry - y); ny = y; nw = w + (rx - x); nx = x; break;
          case 'ne': nh = h + (ry - y); ny = y; nw = x - rx; break;
          case 'sw': nh = y - ry; nw = w + (rx - x); nx = x; break;
          case 'se': nh = y - ry; nw = x - rx; break;
          default: break;
        }
        // Clamp
        nx = Math.max(0, Math.min(width - 1, nx));
        ny = Math.max(0, Math.min(height - 1, ny));
        nw = Math.max(5, Math.min(width - nx, nw));
        nh = Math.max(5, Math.min(height - ny, nh));
        setCropRect({ x: nx, y: ny, w: nw, h: nh });
      }
    };
    const handleUp = () => {
      setCropAction(null);
      setCropStartPoint(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [cropMode, cropAction, cropStartPoint, cropRect, width, height]);
  // Apply crop
  const handleApplyCrop = () => {
    if (!cropRect || cropRect.w < 5 || cropRect.h < 5) return;
    const x = Math.round(cropRect.x);
    const y = Math.round(cropRect.y);
    const w = Math.round(cropRect.w);
    const h = Math.round(cropRect.h);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => {
      ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
      const croppedDataUrl = canvas.toDataURL();
      setImage(croppedDataUrl);
      setWidth(w);
      setHeight(h);
      setPreviewUrl(croppedDataUrl);
      setImgInfo((info) => ({ ...info, width: w, height: h, size: Math.round((croppedDataUrl.length * 3) / 4) }));
    };
    img.src = image;
    setCropRect(null);
    setCropMode(false);
    setShowCropBtn(true); // Show Crop button again
  };
  // Cancel crop
  const handleCancelCrop = () => {
    setCropRect(null);
    setCropMode(false);
    setShowCropBtn(true); // Show Crop button again
  };
  // Text edit state
  const [showTextEdit, setShowTextEdit] = useState(false);
  const [text, setText] = useState('');
  const [textX, setTextX] = useState(50);
  const [textY, setTextY] = useState(50);
  const [textSize, setTextSize] = useState(32);
  const [textColor, setTextColor] = useState('#ff2222');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) handleFile(file);
    // else ignore non-image files
  };

  const handleFile = (file) => {
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImage(ev.target.result);
        setPreviewUrl(ev.target.result);
        // Get image dimensions
        const img = new window.Image();
        img.onload = () => {
          setWidth(img.width);
          setHeight(img.height);
          setImgInfo({
            width: img.width,
            height: img.height,
            size: file.size
          });
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    }
  };
  // Low Quality button handler
  const handleLowQuality = () => {
    setWidth(300);
    setHeight(300);
    setRotate(0); // Reset rotation to original
    setTimeout(handleResize, 0);
  };

  const drawImage = (img, w, h, rot, fh, fv, bright, cont, drawText = true) => {
    const canvas = canvasRef.current;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.save();
    // Move to center for rotation/flip
    ctx.translate(w / 2, h / 2);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.scale(fh ? -1 : 1, fv ? -1 : 1);
    ctx.filter = `brightness(${bright}%) contrast(${cont}%)`;
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
    // ...existing code...
    // Draw text if enabled
    if (drawText && showTextEdit && text) {
      ctx.save();
      ctx.font = `${textSize}px sans-serif`;
      ctx.fillStyle = textColor;
      ctx.textBaseline = 'top';
      ctx.fillText(text, textX, textY);
      ctx.restore();
    }
    const dataUrl = canvas.toDataURL();
    setPreviewUrl(dataUrl);
    // Update imgInfo size to match new canvas size
    const size = Math.round((dataUrl.length * 3) / 4 - (dataUrl.endsWith('==') ? 2 : dataUrl.endsWith('=') ? 1 : 0));
    setImgInfo((info) => ({ ...info, width: w, height: h, size }));
  };
  // ...existing code...

  const handleResize = () => {
    if (!image) return;
    const img = new window.Image();
    img.onload = () => {
      drawImage(img, width, height, rotate, false, false, brightness, contrast);
    };
    img.src = image;
  };

  // Drag-to-move text state
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Handle click on image to set text position (also start drag)
  const handleImageClick = (e) => {
    if (!showTextEdit) return;
    const imgElem = e.target;
    const rect = imgElem.getBoundingClientRect();
    // Calculate click position relative to image
    const x = ((e.clientX - rect.left) / rect.width) * width;
    const y = ((e.clientY - rect.top) / rect.height) * height;
    // If text exists and click is near text, start drag
    if (text) {
      const textBoxW = text.length * textSize * 0.6;
      const textBoxH = textSize;
      if (
        x >= textX && x <= textX + textBoxW &&
        y >= textY && y <= textY + textBoxH
      ) {
        setDragging(true);
        setDragOffset({ x: x - textX, y: y - textY });
        return;
      }
    }
    setTextX(Math.round(x));
    setTextY(Math.round(y));
  };

  // Handle mouse move for dragging text
  const handleImageMouseMove = (e) => {
    if (!dragging || !showTextEdit) return;
    const imgElem = e.target;
    const rect = imgElem.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * width;
    const y = ((e.clientY - rect.top) / rect.height) * height;
    setTextX(Math.max(0, Math.min(width - 10, Math.round(x - dragOffset.x))));
    setTextY(Math.max(0, Math.min(height - 10, Math.round(y - dragOffset.y))));
  };

  // Handle mouse up to stop dragging
  const handleImageMouseUp = () => {
    if (dragging) setDragging(false);
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;
    let mime = 'image/png';
    let ext = 'png';
    if (downloadFormat === 'jpg' || downloadFormat === 'jpeg') {
      mime = 'image/jpeg';
      ext = 'jpg';
    } else if (downloadFormat === 'webp') {
      mime = 'image/webp';
      ext = 'webp';
    } else if (downloadFormat === 'bmp') {
      mime = 'image/bmp';
      ext = 'bmp';
    }
    const link = document.createElement('a');
    link.download = `edited-image.${ext}`;
    link.href = canvasRef.current.toDataURL(mime);
    link.click();
  };

  const handleRotate = (deg) => {
    setRotate((prev) => prev + deg);
  };
  // Removed handleFlipH and handleFlipV
  const handleBrightness = (val) => {
    setBrightness(val);
    setTimeout(handleResize, 0);
  };
  const handleContrast = (val) => {
    setContrast(val);
    setTimeout(handleResize, 0);
  };

  // Redraw on param change
  React.useEffect(() => {
    if (!image) return;
    const img = new window.Image();
    img.onload = () => {
      // Always clear canvas before drawing
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      drawImage(img, width, height, rotate, false, false, brightness, contrast, true);
    };
    img.src = image;
    // eslint-disable-next-line
  }, [width, height, rotate, brightness, contrast, text, textX, textY, textSize, textColor, showTextEdit]);

  return (
    <div className="tool1page-container">
      <div className="tool1page-card tool1page-flexrow" style={{
        background: 'var(--bg-secondary, #f9f5f5)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: 10,
        boxShadow: '0 1px 4px var(--shadow-color, rgba(35,85,214,0.06))',
        padding: '18px 14px',
        marginTop: 12
      }}>
        <div className="tool1page-maincol">
          <h2 style={{ textAlign: 'center', marginBottom: 12, color: 'var(--text-primary)', letterSpacing: 1 }}>Image Editor Tool</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 500, color: 'var(--text-secondary, #444)' }}>
              Width (px):
              <input
                type="number"
                value={width || 0}
                min={1}
                max={2000}
                onChange={e => setWidth(Number(e.target.value))}
                style={{ width: 70, marginLeft: 8, borderRadius: 6, border: '1px solid var(--border-color)', padding: '4px 8px', background: 'var(--bg-secondary, #fff)', color: 'var(--text-primary)' }}
                disabled={!previewUrl}
              />
            </label>
            <label style={{ fontWeight: 500, color: 'var(--text-secondary, #444)' }}>
              Height (px):
              <input
                type="number"
                value={height || 0}
                min={1}
                max={2000}
                onChange={e => setHeight(Number(e.target.value))}
                style={{ width: 70, marginLeft: 8, borderRadius: 6, border: '1px solid var(--border-color)', padding: '4px 8px', background: 'var(--bg-secondary, #fff)', color: 'var(--text-primary)' }}
                disabled={!previewUrl}
              />
            </label>
            <button onClick={handleResize} style={buttonStyle}>Resize</button>
          </div>
        </div>
        {previewUrl ? (
          <>
            <div
              className="tool1page-image-section"
              style={{
                border: dragActive ? '2.5px dashed #08ff46' : '2px dashed #00ff5e',
                // background: dragActive ? '#2b2d30' :undefined,
                transition: 'border 0.2s, background 0.2s',
                borderRadius: 12
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div style={{position:'relative', display:'inline-block'}}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="crop-img"
                  onClick={cropMode ? undefined : handleImageClick}
                  onMouseDown={cropMode ? handleCropMouseDown : undefined}
                  onMouseMove={handleImageMouseMove}
                  onMouseUp={handleImageMouseUp}
                  onMouseLeave={handleImageMouseUp}
                    onTouchStart={cropMode ? handleCropMouseDown : undefined}
                  style={{cursor: cropMode ? (cropAction ? 'grabbing' : 'crosshair') : (showTextEdit ? (dragging ? 'grabbing' : 'grab') : 'default')}}
                  draggable={false}
                />
                {/* Professional crop overlay with handles */}
                {cropMode && (
                  <>
                    {/* Shaded overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      background: 'rgba(0,0,0,0.25)',
                      pointerEvents: 'none',
                    }} />
                    {/* Crop rectangle and handles */}
                    {cropRect && (
                      <>
                        <div style={{
                          position: 'absolute',
                          left: `${cropRect.x / width * 100}%`,
                          top: `${cropRect.y / height * 100}%`,
                          width: `${cropRect.w / width * 100}%`,
                          height: `${cropRect.h / height * 100}%`,
                          border: '2px solid #22c55e',
                          background: 'rgba(34,197,94,0.10)',
                          boxSizing: 'border-box',
                          pointerEvents: 'none',
                        }} />
                        {/* Handles: corners and sides */}
                        {['nw','n','ne','e','se','s','sw','w'].map((pos) => {
                          // Position handle
                          let style = {position:'absolute', width:14, height:14, background:'#22c55e', borderRadius:7, border:'2px solid #fff', zIndex:2, cursor:'pointer', boxShadow:'0 1px 4px rgba(34,197,94,0.15)'};
                          const x = cropRect.x, y = cropRect.y, w = cropRect.w, h = cropRect.h;
                          switch(pos) {
                            case 'nw': style.left = `${(x-7)/width*100}%`; style.top = `${(y-7)/height*100}%`; style.cursor='nwse-resize'; break;
                            case 'n': style.left = `${(x+w/2-7)/width*100}%`; style.top = `${(y-7)/height*100}%`; style.cursor='ns-resize'; break;
                            case 'ne': style.left = `${(x+w-7)/width*100}%`; style.top = `${(y-7)/height*100}%`; style.cursor='nesw-resize'; break;
                            case 'e': style.left = `${(x+w-7)/width*100}%`; style.top = `${(y+h/2-7)/height*100}%`; style.cursor='ew-resize'; break;
                            case 'se': style.left = `${(x+w-7)/width*100}%`; style.top = `${(y+h-7)/height*100}%`; style.cursor='nwse-resize'; break;
                            case 's': style.left = `${(x+w/2-7)/width*100}%`; style.top = `${(y+h-7)/height*100}%`; style.cursor='ns-resize'; break;
                            case 'sw': style.left = `${(x-7)/width*100}%`; style.top = `${(y+h-7)/height*100}%`; style.cursor='nesw-resize'; break;
                            case 'w': style.left = `${(x-7)/width*100}%`; style.top = `${(y+h/2-7)/height*100}%`; style.cursor='ew-resize'; break;
                            default: break;
                          }
                          return <div key={pos} data-crophandle={pos} style={style} onMouseDown={handleCropMouseDown}></div>;
                        })}
                      </>
                    )}
                  </>
                )}
              </div>
              <div style={{ marginTop: 16 }}>
                <label htmlFor="file-upload" style={{ display: 'inline-block' }}>
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <span
                    style={{
                      background: 'linear-gradient(90deg, #4f8cff 0%, #2355d6 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 18px',
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(79,140,255,0.08)',
                      transition: 'background 0.2s',
                      display: 'inline-block',
                    }}
                  >
                    Choose Image
                  </span>
                </label>
                {fileName && (
                  <span style={{ marginLeft: 14, color: '#2355d6', fontWeight: 500, fontSize: 15, verticalAlign: 'middle' }}>{fileName}</span>
                )}
              </div>
              {dragActive && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(35,85,214,0.08)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2355d6',
                  fontWeight: 600,
                  fontSize: 22,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}>
                  Drop image here
                </div>
              )}
            </div>
            {/* Info and sliders below image section */}
            <div style={{ marginTop: 18, textAlign: 'center' }}>
              {imgInfo.width && imgInfo.height && (
                <div style={{ margin: '0 0 12px 0', color: '#444', fontWeight: 500, fontSize: 15 }}>
                  Size: {imgInfo.width} x {imgInfo.height} px &nbsp; | &nbsp;
                  File: {imgInfo.size < 1024 * 1024
                    ? (imgInfo.size / 1024).toFixed(1) + ' KB'
                    : (imgInfo.size / (1024 * 1024)).toFixed(2) + ' MB'}
                </div>
              )}
              <div className="tool1page-sliders">
                <label style={{ fontWeight: 500, color: '#444' }}>Brightness:
                  <input type="range" min={0} max={200} value={brightness} onChange={e => handleBrightness(Number(e.target.value))} style={{ marginLeft: 8 }} />
                  <span style={{ marginLeft: 8 }}>{brightness}%</span>
                </label>
                <label style={{ fontWeight: 500, color: '#444' }}>Contrast:
                  <input type="range" min={0} max={200} value={contrast} onChange={e => handleContrast(Number(e.target.value))} style={{ marginLeft: 8 }} />
                  <span style={{ marginLeft: 8 }}>{contrast}%</span>
                </label>
              </div>
              {/* Text edit controls */}
              <div style={{ margin: '18px 0 0 0', textAlign: 'center' }}>
                <button style={{...featureBtnStyle, minWidth: 110}} onClick={() => setShowTextEdit(v => !v)}>
                  {showTextEdit ? 'Hide Text Edit' : 'Add Text'}
                </button>
              </div>
              {showTextEdit && (
                <div className="tool1page-textedit">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label style={{ fontWeight: 500, color: '#444', display: 'flex', alignItems: 'center', gap: 8 }}>
                      Text:
                      <input type="text" value={text} onChange={e => setText(e.target.value)} style={{ flex: 1, borderRadius: 6, border: '1px solid #ccc', padding: '4px 8px' }} />
                    </label>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <label style={{ fontWeight: 500, color: '#444' }}>Font Size:
                        <input type="number" min={8} max={200} value={textSize} onChange={e => setTextSize(Number(e.target.value))} style={{ width: 60, marginLeft: 8, borderRadius: 6, border: '1px solid #ccc', padding: '4px 8px' }} />
                      </label>
                      <label style={{ fontWeight: 500, color: '#444' }}>Color:
                        <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} style={{ marginLeft: 8 }} />
                      </label>
                      <label style={{ fontWeight: 500, color: '#444' }}>X:
                        <input type="number" min={0} max={width-10} value={textX} onChange={e => setTextX(Number(e.target.value))} style={{ width: 60, marginLeft: 8, borderRadius: 6, border: '1px solid #ccc', padding: '4px 8px' }} />
                      </label>
                      <label style={{ fontWeight: 500, color: '#444' }}>Y:
                        <input type="number" min={0} max={height-10} value={textY} onChange={e => setTextY(Number(e.target.value))} style={{ width: 60, marginLeft: 8, borderRadius: 6, border: '1px solid #ccc', padding: '4px 8px' }} />
                      </label>
                    </div>
                    <button style={{...featureBtnStyle, background: '#ffdddd', color: '#d62222', minWidth: 80}} onClick={() => { setText(''); }}>Remove Text</button>
                  </div>
                </div>
              )}
            </div>
            {/* Feature buttons group below info/sliders */}
            <div className="tool1page-feature-btns" style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
              <button style={featureBtnStyle} disabled={!previewUrl} onClick={() => handleRotate(-90)}>Rotate Left</button>
              <button style={featureBtnStyle} disabled={!previewUrl} onClick={() => handleRotate(90)}>Rotate Right</button>
              <button style={featureBtnStyle} disabled={!previewUrl} onClick={handleLowQuality}>Low Quality</button>
              {/* Crop button always visible, disabled if no image or cropMode active */}
              {showCropBtn && (
                <button
                  style={featureBtnStyle}
                  disabled={!previewUrl || cropMode}
                  onClick={() => {
                    setCropMode(true);
                    setCropRect({ x: 0, y: 0, w: width, h: height });
                    setShowCropBtn(false); // Hide Crop button
                  }}
                >
                  Crop
                </button>
              )}
              {cropMode && (
                <>
                  <button style={featureBtnStyle} onClick={handleApplyCrop} disabled={!cropRect || cropRect.w < 5 || cropRect.h < 5}>Apply Crop</button>
                  <button style={featureBtnStyle} onClick={handleCancelCrop}>Cancel</button>
                </>
              )}
              {/* Format select */}
              <select value={downloadFormat} onChange={e => setDownloadFormat(e.target.value)} style={{padding:'7px 10px',borderRadius:6,border:'1.5px solid #bfc7d1',fontWeight:600,fontSize:14,minWidth:80}}>
                <option value="png">PNG</option>
                <option value="jpg">JPG</option>
                <option value="webp">WEBP</option>
                <option value="bmp">BMP</option>
              </select>
              <button style={{
                ...featureBtnStyle,
                fontSize: 14,
                padding: '7px 18px',
                minWidth: 90,
                background: '#22c55e',
                color: '#fff',
                border: '1.5px solid #16a34a',
                boxShadow: '0 2px 8px rgba(34,197,94,0.08)'
              }} disabled={!previewUrl} onClick={handleDownload}>Download</button>
            </div>
          </>
        ) : (
          <>
            <div
              className="tool1page-image-section"
              style={{
                minHeight: 220,
                background: dragActive ? '#093da5' : '#bdbdcb',
                border: dragActive ? '2.5px dashed #FFD600' : '2px dashed #FFD600',
                color: '#f7f9fc',
                fontSize: 20,
                fontWeight: 500,
                letterSpacing: 1,
                flexDirection: 'column',
                position: 'relative',
                transition: 'border 0.2s, background 0.2s',
                borderRadius: 12
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div>No image selected</div>
              <div style={{ marginTop: 16 }}>
                <label htmlFor="file-upload" style={{ display: 'inline-block' }}>
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <span
                    style={{
                      background: 'linear-gradient(90deg, #4f8cff 0%, #2355d6 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 18px',
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(79,140,255,0.08)',
                      transition: 'background 0.2s',
                      display: 'inline-block',
                    }}
                  >
                    Choose Image
                  </span>
                </label>
                {fileName && (
                  <span style={{ marginLeft: 14, color: '#2355d6', fontWeight: 500, fontSize: 15, verticalAlign: 'middle' }}>{fileName}</span>
                )}
              </div>
              {dragActive && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(35,85,214,0.08)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2355d6',
                  fontWeight: 600,
                  fontSize: 22,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}>
                  Drop image here
                </div>
              )}
            </div>
            {/* Feature buttons group below image (disabled) */}
            <div className="tool1page-feature-btns">
              <button style={featureBtnStyle} disabled>Rotate Left</button>
              <button style={featureBtnStyle} disabled>Rotate Right</button>
              <button style={featureBtnStyle} disabled>Low Quality</button>
              {/* Crop button always visible, disabled if no image */}
              <button style={featureBtnStyle} disabled>Crop</button>
              <button style={{
                ...featureBtnStyle,
                fontSize: 14,
                padding: '7px 18px',
                minWidth: 90,
                background: '#22c55e',
                color: '#fff',
                border: '1.5px solid #16a34a',
                boxShadow: '0 2px 8px rgba(34,197,94,0.08)'
              }} disabled>Download</button>
            </div>
          </>
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
        <div className="tool1page-featurecol">
          <div className="tool1page-featurelist" style={{
            background: 'var(--bg-secondary, #fff)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 10,
            boxShadow: '0 1px 4px var(--shadow-color, rgba(35,85,214,0.06))',
            padding: '18px 14px',
            marginTop: 12
          }}>
            <h3 className="tool1page-featuretitle">Features</h3>
            <ul>
              <li>Image upload &amp; preview</li>
              <li>Resize (width/height px)</li>
              <li>Rotate (left/right)</li>
              <li>Flip (horizontal/vertical)</li>
              <li>Brightness/contrast adjust (slider)</li>
              <li>Crop image (with draggable rectangle)</li>
              <li>Add text overlay (color, size, drag to position)</li>
              <li>Move/drag text on image</li>
              <li>Reset to original</li>
              <li>Download edited image</li>
              <li>Show image size (px) and file size (KB/MB)</li>
              <li>Coming soon: Undo/Redo, Filters, Draw/Annotate, Stickers, Export as JPG/WebP, Share, Zoom, Multi-language</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const buttonStyle = {
  background: 'linear-gradient(90deg, #4f8cff 0%, #2355d6 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 7,
  padding: '5px 12px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(79,140,255,0.08)',
  transition: 'background 0.2s',
};

export default Tool1Page;
